import { FC, useEffect, useState } from 'react';
import {
  Box,
  Text,
  HStack,
  useColorModeValue,
  Flex,
  Menu,
  MenuButton,
  IconButton,
  Portal,
  MenuList,
  MenuItem,
  Input,
} from '@chakra-ui/react';
import { faEllipsisH, faPen } from '@fortawesome/free-solid-svg-icons';
import { EventGroupOrCheckpoint } from './ProjectHistoryTimeline';
import { ago } from 'src/utils/time-utils';
import UserBadge from 'src/components/common/UserBadge';
import {
  EventType,
  HistoryEventDeepFragment,
  HistoryEventGroupDeepFragment,
} from 'src/gql/graphql';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';

// Props for the EventContent component
interface EventContentProps {
  eventType: EventType;
  oldName?: string;
  newName?: string;
  details?: string;
  secondaryTextColor: string;
}

const EventContent: FC<EventContentProps> = ({
  eventType,
  oldName,
  newName,
  details,
  secondaryTextColor,
}) => {
  if (eventType === EventType.Modified && oldName && newName) {
    return (
      <Text fontSize="xs" color={secondaryTextColor} mb="1">
        <Text as="del">{oldName}</Text> → {newName}
      </Text>
    );
  }

  if (details) {
    return (
      <Text fontSize="xs" color={secondaryTextColor} mb="1">
        {details}
      </Text>
    );
  }

  return null;
};

// Props for the TimelineEvent component
interface ProjectHistoryTimelineEventProps {
  eventGroupOrCheckpoint: EventGroupOrCheckpoint;
  isSelected: boolean;
  isPreview?: boolean;
  onClick: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  t: (key: string, options?: { count: number }) => string;
  handleCheckpointRename: (checkpointName: string) => Promise<void>;
}

/* eslint-disable complexity */
export const ProjectHistoryTimelineEvent: FC<
  ProjectHistoryTimelineEventProps
> = ({
  eventGroupOrCheckpoint,
  isSelected,
  isPreview = false,
  onClick,
  onMouseDown,
  handleCheckpointRename,
  t,
}) => {
  const eventGroup = eventGroupOrCheckpoint as HistoryEventGroupDeepFragment;
  const checkpoint = eventGroupOrCheckpoint as HistoryEventDeepFragment;

  const {
    eventType,
    resourceType,
    author,
    isCheckpoint,
    affectedResources,
    firstEvent,
    eventCount,
  } = eventGroup;
  const { eventName } = checkpoint;

  const date = eventGroup.groupDate ?? checkpoint.createdAt;

  const formattedResourceType = resourceType?.includes('SHEET_SHAPE')
    ? 'shape'
    : resourceType;

  const translationKey =
    `group-name.${formattedResourceType}.${eventType}`.toLowerCase();

  const eventGroupName =
    isCheckpoint || firstEvent?.isCheckpoint
      ? eventName ?? firstEvent?.eventName
      : t(translationKey, {
          count: affectedResources ?? 0,
        });

  // Theme colors
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const selectedTextColor = useColorModeValue('blue.700', 'blue.200');
  const previewTextColor = useColorModeValue('gray.600', 'gray.400');
  const checkPointColor = useColorModeValue('cyan.500', 'cyan.200');

  const [isRenaming, setIsRenaming] = useState(false);
  const [newCheckpointName, setNewCheckpointName] = useState(
    eventGroupName ?? ''
  );

  useEffect(() => {
    setNewCheckpointName((prev) => {
      if (!eventGroupName || prev === eventGroupName) return prev;
      return eventGroupName;
    });
  }, [eventGroupName]);

  // Get the right text color based on selection state
  const primaryColor = isCheckpoint
    ? checkPointColor
    : isSelected
    ? selectedTextColor
    : isPreview
    ? previewTextColor
    : textColor;

  return (
    <Box
      py="2"
      px="4"
      cursor="pointer"
      onClick={onClick}
      onMouseDown={onMouseDown}
      position="relative"
      role="group"
    >
      <Box py="1">
        <Flex mb="1" justifyContent="space-between">
          {isRenaming ? (
            <Input
              aria-label="Rename checkpoint"
              type="text"
              textAlign={'start'}
              size="sm"
              px={1}
              borderRadius={2}
              borderColor="chakra-border-color"
              autoFocus={true}
              onFocus={(e) => e.target.select()}
              placeholder="Checkpoint Name"
              value={newCheckpointName}
              onChange={(e) => {
                setNewCheckpointName(e.target.value);
              }}
              onBlur={() => {
                setIsRenaming(false);
                handleCheckpointRename(newCheckpointName);
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setIsRenaming(false);
                  handleCheckpointRename(newCheckpointName);
                } else if (e.key === 'Escape') {
                  setNewCheckpointName(eventGroupName ?? '');
                  setIsRenaming(false);
                }
              }}
            />
          ) : (
            <>
              <Text fontSize="xs" fontWeight="bold" color={primaryColor}>
                {eventGroupName}
              </Text>
              {date && (
                <Text fontSize="xs" color={secondaryTextColor}>
                  {ago(date)}
                </Text>
              )}
            </>
          )}
        </Flex>
        <EventContent
          eventType={eventType || EventType.Modified}
          oldName={eventCount === 1 ? firstEvent?.metadata?.oldName : undefined}
          newName={eventCount === 1 ? firstEvent?.metadata?.newName : undefined}
          // Not sure what to use the details for yet, and how to handle translations
          // details={eventCount === 1 ? firstEvent?.metadata?.details : undefined}
          secondaryTextColor={secondaryTextColor}
        />

        <HStack justifyContent="space-between">
          <HStack spacing="1" mt="1">
            <Text fontSize="xs" color={secondaryTextColor}>
              {t('event.created-by')}
            </Text>
            <UserBadge username={author ?? 'Unknown author'} size="2xs" />
          </HStack>
          {isCheckpoint ? (
            <Menu isLazy>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    opacity={isOpen ? 1 : 0}
                    _groupHover={{
                      opacity: 1,
                    }}
                    isRound
                    as={IconButton}
                    colorScheme="gray"
                    size={'xs'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <SparkelIcon icon={faEllipsisH} />
                  </MenuButton>

                  <Portal>
                    <MenuList maxW="xs" zIndex={99}>
                      <MenuItem
                        icon={<SparkelIcon icon={faPen} fixedWidth />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsRenaming(true);
                        }}
                      >
                        Rename
                      </MenuItem>
                    </MenuList>
                  </Portal>
                </>
              )}
            </Menu>
          ) : null}
        </HStack>
      </Box>
    </Box>
  );
};
